import { useMemo } from "react";
import get from "lodash/get";
import { useSelector } from "react-redux";
import { ConfigurationResponseProperties } from "@idw-types/data";
import { FormikErrors } from "formik";
import { ButtonTypes } from "@iddataweb/idw-common-ui/enums";
import { AXNUIContent, AXNUITheme } from "@iddataweb/idw-common-ui/types";
import { Logger } from "@utils/logger";
import { ConfirmButton } from "./confirm-button";
import {
    getCancelButtonURL,
    getUseExplicitConsentInput,
    getTmxInDCUI,
    getTMXSessionId
} from "@redux/selectors/verification-configuration-selectors";
import { getAsyncButtonText, getDisableAsync, getExplicitConsentChecked } from "@redux/selectors/verification-form-selectors";
import styles from "./form-button-container.less";

type FormButtonContainerProps = {
    useThemedUI: boolean;
    showAsyncButton: boolean;
    pageProperties: Partial<ConfigurationResponseProperties>;
    hasFinishedTMXProfiling: boolean;
    theme: AXNUITheme;
    content: AXNUIContent;
    errors: FormikErrors<any>;
    isSubmitting: boolean;
    isValid: boolean;
};

export const FormButtonContainer = (props: FormButtonContainerProps) => {
    const {
        useThemedUI,
        showAsyncButton,
        pageProperties,
        hasFinishedTMXProfiling,
        theme,
        content,
        errors,
        isSubmitting,
        isValid
    } = props;

    const cancelButtonURL = useSelector(getCancelButtonURL);
    const asyncButtonText = useSelector(getAsyncButtonText);
    const tmxSessionID = useSelector(getTMXSessionId);
    const tmxInDCUI = useSelector(getTmxInDCUI);
    const disableAsync = useSelector(getDisableAsync);
    const explicitConsentChecked = useSelector(getExplicitConsentChecked);
    const useExplicitConsentInput = useSelector(getUseExplicitConsentInput);

    const isSubmitButtonDisabled = useMemo(() => {
        const formHasErrors = Object.keys(errors || {}).length > 0;
        if (!!tmxSessionID && !tmxInDCUI && !hasFinishedTMXProfiling) {
            return true;
        }
        return formHasErrors || isSubmitting || (!formHasErrors && !isValid) || (useExplicitConsentInput && !explicitConsentChecked);
    }, [
        errors,
        isSubmitting,
        isValid,
        tmxSessionID,
        tmxInDCUI,
        hasFinishedTMXProfiling,
        explicitConsentChecked,
        useExplicitConsentInput
    ]);

    const handleCancelButton = () => {
        if (pageProperties.cancelButtonURL) {
            let url = pageProperties.cancelButtonURL;

            if (!/^(http:\/\/|https:\/\/)/.test(url)) {
                url = 'http://' + url;
            }
            Logger.info(`[AttributeInputForm] Cancel invoked, user will be redirected to: ${url}`);
            window.location.href = url;
        }
    };

    const syncButtonTextInternal = useMemo(() => {
        if (isSubmitting) {
            return `${content.common.button.continued}...`;
        } else if (useThemedUI) {
            const useCustomText = theme.confirmButton?.useCustomText || false;
            if (useCustomText) {
                return content.confirmButton?.buttonText ||
                    (theme.confirmButton?.type
                        //@ts-ignore
                        ? content.submitButtonType[theme.confirmButton.type]
                        : content.common.button.confirm);
            } else {
                return theme.confirmButton?.type
                    //@ts-ignore
                    ? content.submitButtonType[theme.confirmButton.type]
                    : content.common.button.confirm;
            }
        } else {
            return pageProperties.submitButtonType
                //@ts-ignore
                ? content.submitButtonType[pageProperties.submitButtonType]
                : content.common.button.confirm;
        }
    }, [isSubmitting, useThemedUI, theme, content]);

    const asyncButtonTextInternal = useMemo(() => {
        if (isSubmitting) {
            return `${content.common.button.continued}...`;
        } else if (useThemedUI && content.confirmButton.asyncButtonText) {
            return content.confirmButton.asyncButtonText;
        } else {
            return get(content, asyncButtonText) || content.common.button.async;
        }
    }, [isSubmitting, useThemedUI, asyncButtonText, content]);

    const containerStyle = useMemo(() => {
        if (cancelButtonURL) {
            return styles.formButtonContainerTwoButton;
        }

        return styles.formButtonContainer;
    }, [cancelButtonURL])

    return (
        <div className={containerStyle} id="form-button-container">
            {!showAsyncButton && (
                <ConfirmButton
                    id="verify-all"
                    className="verify-button verify-sync"
                    size="lg"
                    color={!theme.confirmButton ? 'primary' : 'default'}
                    isSubmitButton={true}
                    disabled={isSubmitButtonDisabled}
                    isSubmitting={isSubmitting}
                    backgroundColor={theme.confirmButton?.backgroundColor}
                    borderWidth={theme.confirmButton?.borderWidth}
                    width={theme.confirmButton?.width}
                    textColor={theme.confirmButton?.textColor}
                    height={theme.confirmButton?.height}
                    cornerRadius={theme.confirmButton?.cornerRadius}
                    buttonText={syncButtonTextInternal}
                />
            )}
            {showAsyncButton && (
                <ConfirmButton
                    id="async-submit"
                    size="lg"
                    className="verify-button verify-async"
                    color={!theme.confirmButton ? 'primary' : 'default'}
                    isSubmitButton={true}
                    disabled={disableAsync || isSubmitButtonDisabled}
                    isSubmitting={isSubmitting}
                    backgroundColor={theme.confirmButton?.backgroundColor}
                    borderWidth={theme.confirmButton?.borderWidth}
                    width={theme.confirmButton?.width}
                    textColor={theme.confirmButton?.textColor}
                    height={theme.confirmButton?.height}
                    cornerRadius={theme.confirmButton?.cornerRadius}
                    buttonText={asyncButtonTextInternal}
                />
            )}
            {cancelButtonURL && (
                <ConfirmButton
                    id="cancel-button"
                    size="lg"
                    className="cancel-button"
                    type={ButtonTypes.BUTTON}
                    color={!theme.confirmButton ? 'primary' : 'default'}
                    backgroundColor={theme.confirmButton?.textColor}
                    borderWidth={theme.confirmButton?.borderWidth}
                    width={theme.confirmButton?.width}
                    textColor={theme.confirmButton?.backgroundColor}
                    height={theme.confirmButton?.height}
                    borderColor={theme.confirmButton?.backgroundColor}
                    cornerRadius={theme.confirmButton?.cornerRadius}
                    onClick={handleCancelButton}
                    buttonText={content.common.button.cancel}
                />
            )}
        </div>
    );
};